import * as React from 'react';

import { getStorageCachedURL } from '@gitbook/app-database';
import type { BlockFileData } from '@gitbook/doc-core';
import { useHiveRevision } from '@gitbook/hive-react';
import { MediaCard } from '@gitbook/react-uikit';
import type { BlockProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { filterOutNullable } from '@gitbook/util-ts';

import { getFileType, getHumanFileSize } from '../../../utils';
import { FileIcon } from '../../Files';
import { useInsertFiles } from '../../InsertFilesDialog';
import { EditableCaption, useCaption } from '../caption';
import {
    BlockLoadingFallback,
    BlockWrapper,
    useBlockPaletteFactory,
    VoidBlockFrame,
} from '../common';

/**
 * Renderer for a block file (in an images gallery or files)
 */
export function BlockFile(props: BlockProps) {
    const { node, editor } = props;
    const insertFiles = useInsertFiles();

    /** Prompt user to select a new file for this block */
    const onReplace = async () => {
        const [ref] = await insertFiles.prompt({
            title: 'Replace file',
        });

        if (ref?.kind !== 'file') {
            return;
        }

        editor.change((change) => {
            change.insertDataByKey(node.key, ['ref'], ref);
        });
    };

    const caption = useCaption(node, editor);
    const palette = useBlockPaletteFactory({
        nodeKey: node.key,
        createExtraGroups: () => [
            {
                key: 'types',
                commands: () => {
                    return [
                        {
                            key: 'files',
                            title: 'Replace file…',
                            icon: Icon.Paperclip,
                            trigger: () => {
                                onReplace();
                            },
                        },
                        {
                            key: 'caption',
                            title: caption.hasCaption ? 'Edit caption' : 'Add caption',
                            icon: Icon.Caption,
                            trigger: () => {
                                caption.selectCaption();
                            },
                        },
                        caption.hasCaption
                            ? {
                                  key: 'caption-remove',
                                  title: 'Remove caption',
                                  icon: Icon.CaptionRemove,
                                  trigger: () => {
                                      caption.removeCaption();
                                  },
                              }
                            : null,
                    ].filter(filterOutNullable);
                },
            },
        ],
    });

    return (
        <BlockWrapper {...props} palette={palette}>
            <VoidBlockFrame {...props}>
                <React.Suspense fallback={<BlockLoadingFallback />}>
                    <FileCard {...props} onReplace={onReplace} />
                </React.Suspense>
            </VoidBlockFrame>
            <EditableCaption {...props} />
        </BlockWrapper>
    );
}

function FileCard(props: BlockProps & { onReplace?: () => void }) {
    const { node, editor, onReplace } = props;

    const { ref } = node.data.toFrozenObject() as BlockFileData;

    const { revision } = useHiveRevision();
    const file = revision.files[ref.file];

    /** When pressing the file, download or select if file has been deleted */
    const onPress = () => {
        if (file) {
            window.open(getStorageCachedURL(file.downloadURL), '_blank');
        } else if (!editor.readOnly && onReplace) {
            onReplace();
        }
    };

    return (
        <MediaCard
            onPress={onPress}
            icon={<FileIcon file={file} zoomable />}
            title={file ? file.name : 'File has been deleted'}
            subtitle={file ? getHumanFileSize(file.size) : undefined}
            details={file ? getFileType(file) : undefined}
        />
    );
}
